import { alpha, Box, Theme } from '@mui/material';
import { useAppBuilderContext } from 'app/app/editor/context';
import LayoutNode from 'app/app/editor/tabs/elements/layout/LayoutNode';
import { useLayoutContextMenu } from 'app/app/editor/tabs/elements/layout/useLayoutContextMenu';
import FlatList from 'app/app/editor/tabs/elements/layout/FlatList';
import { DndProvider } from 'react-dnd';
import {
    getBackendOptions,
    MultiBackend,
    NodeModel,
    Tree as MinoruTree,
    TreeMethods,
} from '@minoru/react-dnd-treeview';
import React, { useMemo, useRef } from 'react';
import { IComponentNode } from 'app/app/editor/index.functions';
import insertNodeIntoPage from 'app/app/editor/page/insertNodeIntoPage';
import { getNodeByUid } from 'app/app/editor/page/getNodeByUid';
import styles from 'components/common/tree/Tree.styles';
type TreeType = NodeModel<IComponentNode>;

export default function LayoutTree({ componentUid }: { componentUid: string | null }) {
    const ctx = useAppBuilderContext();
    const layout = ctx.page?.layout;

    const { handleOpenContextMenu, contextMenu } = useLayoutContextMenu();

    const treeData: Array<TreeType> = useMemo(() => {
        const nodeToModel = (node: IComponentNode, parentNode?: IComponentNode): Array<TreeType> => {
            const result: Array<TreeType> = [
                {
                    id: node.params.uid,
                    parent: parentNode ? parentNode.params.uid : 0,
                    droppable: node.type == 'container',
                    text: node.params.uid,
                    data: node,
                },
            ];

            return result.concat(
                node.elements?.map((n) => nodeToModel(n, node)).reduce((a, b) => a.concat(b), []) ?? [],
            );
        };
        return nodeToModel(layout);
    }, [layout]);

    const handleDrop = (source: IComponentNode, target: IComponentNode, index?: number) => {
        const node = getNodeByUid(ctx.page.layout, source.params.uid);
        if (!node) {
            return;
        }
        const newPage = insertNodeIntoPage(ctx.page, node, target.params.uid, index, true);
        ctx.setPage(newPage, false);
        ctx.sendMessage({ name: 'reloadPage' });
    };

    const ref = useRef<TreeMethods>(null);

    if (!layout) {
        return null;
    }
    return (
        <Box>
            {contextMenu}
            {ctx.withTemplate ? (
                <FlatList node={layout} />
            ) : (
                <Box sx={styles.holder}>
                    <DndProvider backend={MultiBackend} options={getBackendOptions()}>
                        <MinoruTree
                            sort={false}
                            insertDroppableFirst={false}
                            tree={treeData}
                            ref={ref}
                            rootId={0}
                            canDrag={(node) => {
                                return node?.data?.subtype != 'page_menu';
                            }}
                            canDrop={(tree, { dragSource, dropTargetId }) => {
                                if (dragSource?.parent === dropTargetId) {
                                    return true;
                                }
                            }}
                            onDrop={(_: any, options) => {
                                if (!options?.dragSource?.data || !options.dropTarget?.data) {
                                    return;
                                }
                                handleDrop(options.dragSource.data, options.dropTarget.data, options.relativeIndex);
                            }}
                            initialOpen={true}
                            placeholderRender={(node, { depth }) => <Placeholder depth={depth} />}
                            render={(node: TreeType, params) => {
                                if (!node.data) {
                                    return <></>;
                                }
                                return (
                                    <LayoutNode
                                        handleOpenContextMenu={handleOpenContextMenu}
                                        node={node.data}
                                        componentUid={componentUid}
                                        isOpen={params.isOpen}
                                        toggleOpen={params.onToggle}
                                    />
                                );
                            }}
                            classes={{
                                root: 'tree-mi',
                                container: 'tree-mi__list',
                                listItem: 'tree-mi__item',
                                dropTarget: 'tree-mi__drop-target',
                                draggingSource: 'tree-mi__dragging-source',
                                placeholder: 'tree-mi__placeholder',
                            }}
                        />
                    </DndProvider>
                </Box>
            )}
        </Box>
    );
}

function Placeholder(props: { depth: number }) {
    return (
        <Box
            sx={{
                height: '16px',
                left: props.depth * 24 + 'px',
                border: (theme: Theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
            }}
        ></Box>
    );
}
